import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import pb, { apiCall } from '../services/pocketbase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';

const {
  FiEdit3,
  FiArrowLeft,
  FiExternalLink,
  FiCopy,
  FiCheck,
  FiClock,
  FiTag,
  FiLayers,
  FiMonitor,
  FiUsers,
  FiImage,
  FiAlertCircle
} = FiIcons;

const VideoDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedField, setCopiedField] = useState('');

  useEffect(() => {
    loadVideo();
  }, [id]);

  const loadVideo = async () => {
    try {
      setLoading(true);
      setError('');
      const record = await apiCall(() => pb.collection('videos').getOne(id, {
        expand: 'app_ids'
      }));
      setVideo(record);
    } catch (loadError) {
      console.error('Error loading video:', loadError);
      setError('Could not load this video record.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      window.setTimeout(() => setCopiedField(''), 1600);
    } catch (copyError) {
      console.error('Could not copy to clipboard:', copyError);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Loading...</div>;
  if (error) {
    return (
      <div className="max-w-xl mx-auto p-8 bg-red-500/10 border border-red-500/20 rounded-xl text-red-200 flex items-center gap-3">
        <SafeIcon icon={FiAlertCircle} className="w-5 h-5 flex-shrink-0" />
        <span>{error}</span>
      </div>
    );
  }
  if (!video) return <div className="p-8 text-center text-slate-400">Video not found</div>;

  const audience = video.visibility_scope === 'client_facing'
    ? video.client_audience_role
    : video.internal_audience_role;

  return (
    <div className="max-w-5xl mx-auto pb-12">
      <div className="flex items-center justify-between mb-8 gap-4">
        <div className="flex items-center space-x-4 min-w-0">
          <button onClick={() => navigate('/videos')} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors flex-shrink-0">
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-white truncate">{video.title}</h1>
        </div>
        <Link
          to={`/videos/${id}/edit`}
          className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-2 rounded-lg flex items-center space-x-2 font-medium transition-colors shadow-lg shadow-blue-900/20 whitespace-nowrap"
        >
          <SafeIcon icon={FiEdit3} className="w-4 h-4" />
          <span>Edit</span>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-slate-900 rounded-xl overflow-hidden border border-slate-800 shadow-lg">
            <div className="aspect-video bg-slate-950 relative group">
              {video.thumbnail_url ? (
                <img
                  src={video.thumbnail_url}
                  alt={video.title}
                  className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-700">
                  <SafeIcon icon={FiImage} className="w-12 h-12" />
                </div>
              )}

              {video.video_url && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <a
                    href={video.video_url}
                    target="_blank"
                    rel="noreferrer"
                    className="bg-slate-950/70 backdrop-blur-md p-4 rounded-full hover:bg-slate-950/90 transition-all transform hover:scale-105 border border-white/10"
                    title="Open video"
                  >
                    <SafeIcon icon={FiExternalLink} className="w-8 h-8 text-white" />
                  </a>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-800 space-y-3 bg-slate-900/50">
              <CopyRow
                label="Video URL"
                value={video.video_url}
                copied={copiedField === 'video_url'}
                onCopy={() => copyToClipboard(video.video_url, 'video_url')}
              />
              <CopyRow
                label="Thumbnail URL"
                value={video.thumbnail_url}
                copied={copiedField === 'thumbnail_url'}
                onCopy={() => copyToClipboard(video.thumbnail_url, 'thumbnail_url')}
              />
            </div>
          </div>

          <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
            <h3 className="text-lg font-semibold text-white mb-4">Description</h3>
            <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">{video.description || 'No description'}</p>
          </div>

          {(video.keywords || video.tags?.length > 0) && (
            <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
              <h3 className="text-lg font-semibold text-white mb-4">Discoverability</h3>
              {video.keywords && <p className="text-sm text-slate-400 mb-3"><span className="text-slate-300 font-medium">Keywords:</span> {video.keywords}</p>}
              {video.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {video.tags.map(tag => (
                    <span key={tag} className="bg-slate-800 text-slate-300 px-3 py-1 rounded-full text-xs border border-slate-700">{tag}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Details</h3>

            <div className="space-y-4">
              <DetailRow icon={FiLayers} label="Status" value={video.workflow_status?.replace(/_/g, ' ')} />
              <DetailRow icon={FiMonitor} label="Scope" value={video.visibility_scope?.replace(/_/g, ' ')} />
              <DetailRow icon={FiUsers} label="Audience" value={audience?.replace(/_/g, ' ') || 'Not set'} />
              <DetailRow icon={FiTag} label="Category" value={video.category || 'Not set'} />
              <DetailRow
                icon={FiClock}
                label="Updated"
                value={video.updated ? format(new Date(video.updated), 'MMM d, yyyy') : '-'}
                last
              />
            </div>
          </div>

          <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Linked Apps</h3>
            <div className="flex flex-wrap gap-2">
              {video.expand?.app_ids?.length > 0 ? (
                video.expand.app_ids.map(app => (
                  <span key={app.id} className="bg-slate-800 text-blue-300 px-3 py-1 rounded-full text-xs border border-slate-700">
                    {app.app_name}
                  </span>
                ))
              ) : (
                <span className="text-slate-500 text-sm italic">No apps linked</span>
              )}
            </div>
          </div>

          <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">Channels</h3>
            <div className="flex flex-wrap gap-2">
              {video.distribution_channels?.length > 0 ? (
                video.distribution_channels.map(channel => (
                  <span key={channel} className="bg-slate-800 text-purple-300 px-3 py-1 rounded-full text-xs border border-slate-700 capitalize">
                    {channel.replace(/_/g, ' ')}
                  </span>
                ))
              ) : (
                <span className="text-slate-500 text-sm italic">No channels</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CopyRow = ({ label, value, copied, onCopy }) => (
  <div className="flex items-center justify-between gap-4">
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className="text-sm text-slate-400 truncate">{value || 'Not set'}</p>
    </div>
    <button
      type="button"
      onClick={onCopy}
      disabled={!value}
      className="text-blue-400 hover:text-blue-300 disabled:text-slate-600 disabled:cursor-not-allowed text-sm font-medium flex items-center flex-shrink-0"
    >
      <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-4 h-4 mr-1" />
      {copied ? 'Copied' : 'Copy'}
    </button>
  </div>
);

const DetailRow = ({ icon, label, value, last = false }) => (
  <div className={`flex justify-between items-center py-2 ${last ? '' : 'border-b border-slate-800'}`}>
    <span className="text-slate-400 flex items-center">
      <SafeIcon icon={icon} className="w-4 h-4 mr-2" />
      {label}
    </span>
    <span className="text-slate-200 text-sm capitalize text-right ml-4">{value}</span>
  </div>
);

export default VideoDetail;